import type { CloudRestoreReport } from "@board-preserve/contracts";

type ProgressJobStatus =
  | "canceled"
  | "complete"
  | "complete_with_warnings"
  | "failed"
  | "paused"
  | "pending"
  | "running";

type ProgressControlState =
  "active" | "cancel_requested" | "canceled" | "pause_requested" | "paused";

interface ProgressTimeRemainingInput {
  completed: number;
  controlState?: ProgressControlState;
  lastProgressAt?: number | null;
  now?: number;
  startedAt: string | null;
  status: ProgressJobStatus;
  total: number | null;
}

function roundedDuration(remainingMilliseconds: number): string {
  const remainingMinutes = remainingMilliseconds / 60_000;
  if (remainingMinutes < 1.25) return "About a minute remaining";
  if (remainingMinutes < 4) {
    return `About ${Math.max(2, Math.round(remainingMinutes))} minutes remaining`;
  }
  if (remainingMinutes < 15) {
    const minutes = Math.max(4, Math.round(remainingMinutes / 2) * 2);
    return `About ${minutes} minutes remaining`;
  }
  if (remainingMinutes < 60) {
    const minutes = Math.round(remainingMinutes / 5) * 5;
    return `About ${minutes} minutes remaining`;
  }
  if (remainingMinutes < 24 * 60) {
    const hours = Math.max(1, Math.round(remainingMinutes / 30) / 2);
    return `About ${hours} ${hours === 1 ? "hour" : "hours"} remaining`;
  }
  return "More than a day remaining at the current pace";
}

/**
 * Gives users a deliberately broad time estimate based on the job's observed
 * average pace. It never invents an estimate before useful progress exists and
 * does not imply that paused, queued, or terminal work is still counting down.
 */
export function progressTimeRemainingPresentation({
  completed,
  controlState = "active",
  lastProgressAt = null,
  now = Date.now(),
  startedAt,
  status,
  total,
}: ProgressTimeRemainingInput): string {
  if (controlState === "cancel_requested")
    return "Cancelling at a safe checkpoint";
  if (controlState === "pause_requested") return "Pausing at a safe checkpoint";
  if (status === "paused" || controlState === "paused") {
    return "Paused — estimate resumes with the job";
  }
  if (status === "canceled" || controlState === "canceled") return "Cancelled";
  if (status === "failed") return "Stopped before completion";
  if (status === "complete" || status === "complete_with_warnings") {
    return "Finished";
  }
  if (status === "pending" || startedAt === null) return "Waiting to start";
  if (total === null || total <= 0 || completed <= 0) {
    return "Estimating time remaining…";
  }
  if (completed >= total) return "Finishing…";

  if (
    lastProgressAt !== null &&
    Number.isFinite(lastProgressAt) &&
    now - lastProgressAt >= 90_000
  ) {
    return "Delayed — recovering automatically";
  }

  const startedAtMilliseconds = Date.parse(startedAt);
  const elapsedMilliseconds = now - startedAtMilliseconds;
  if (!Number.isFinite(startedAtMilliseconds) || elapsedMilliseconds < 10_000) {
    return "Estimating time remaining…";
  }

  const remainingMilliseconds =
    (elapsedMilliseconds / completed) * (total - completed);
  if (!Number.isFinite(remainingMilliseconds) || remainingMilliseconds <= 0) {
    return "Finishing…";
  }
  return roundedDuration(remainingMilliseconds);
}

export function restoreProgressPresentation(
  status: CloudRestoreReport["status"],
  percentage: number,
): { ariaLabel: string; className: string } {
  const failed = status === "failed";

  return {
    ariaLabel: `Restore progress: ${percentage}%${failed ? "; restore failed" : ""}`,
    className: `job-progress restore-job-progress${failed ? " job-progress-error" : ""}`,
  };
}
